import {
  ArrowRight,
  Award,
  Building2,
  CheckCircle2,
  ExternalLink,
  Gavel,
  Landmark,
  Phone,
  Scale,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'
import SiteFooter from './SiteFooter'
import SiteHeader from './SiteHeader'

const phoneDisplay = '214-754-5500'
const phoneHref = 'tel:+12147545500'
const barProfile =
  'https://www.texasbar.com/am/template.cfm?Template=/Customsource/MemberDirectory/MemberDirectoryDetail.cfm&contactid=161789'

type PracticeSlug = 'commercial-litigation' | 'personal-injury-litigation' | 'real-estate'

type PracticePageData = {
  eyebrow: string
  title: ReactNode
  intro: string
  icon: LucideIcon
  overviewTitle: string
  overview: string[]
  considerationsTitle: string
  considerations: string[]
}

const practicePages: Record<PracticeSlug, PracticePageData> = {
  'commercial-litigation': {
    eyebrow: 'Commercial litigation',
    title: <>Dallas commercial <span>litigation counsel.</span></>,
    intro:
      'The Law Office of Robert W. Buchholz, P.C. provides counsel and representation for business disputes and commercial matters.',
    icon: Gavel,
    overviewTitle: 'Focused counsel for commercial disputes.',
    overview: [
      'Commercial disputes can present important legal, operational, and financial considerations. Understanding the facts, relevant documents, and the goals of the client is an important part of evaluating how to proceed.',
      'Robert W. “Bob” Buchholz brings direct attorney involvement and a deliberate approach to commercial litigation. Each matter is different, and a consultation is necessary to determine whether the firm can assist with a particular dispute.',
    ],
    considerationsTitle: 'Discussing a commercial matter',
    considerations: [
      'The nature and current status of the dispute',
      'Relevant agreements, communications, and other documents',
      'Important dates, deadlines, and business considerations',
      'The client’s questions and objectives',
    ],
  },
  'personal-injury-litigation': {
    eyebrow: 'Personal injury litigation',
    title: <>Dallas personal injury <span>litigation counsel.</span></>,
    intro:
      'The firm provides representation in personal injury litigation with direct attorney involvement and disciplined preparation.',
    icon: ShieldCheck,
    overviewTitle: 'Careful attention to personal injury litigation.',
    overview: [
      'Personal injury litigation can involve significant legal and personal considerations. A careful review of the circumstances is needed before an attorney can assess whether the firm may be able to assist.',
      'Bob Buchholz is Board Certified in Civil Trial Law and has been licensed in Texas since 1979. The firm approaches personal injury litigation with focused preparation, clear communication, and attention to the individual matter.',
    ],
    considerationsTitle: 'Discussing a personal injury matter',
    considerations: [
      'A general, non-confidential description of what occurred',
      'The current status of any claim or legal proceeding',
      'Known dates or deadlines that may require attention',
      'Questions about possible next steps',
    ],
  },
  'real-estate': {
    eyebrow: 'Real estate',
    title: <>Dallas real estate <span>legal counsel.</span></>,
    intro:
      'The Law Office of Robert W. Buchholz, P.C. provides guidance in real estate matters for individuals and businesses.',
    icon: Building2,
    overviewTitle: 'Guidance for real estate matters and disputes.',
    overview: [
      'Real estate matters can involve legal documents, property interests, business considerations, and disputes between parties. The appropriate path depends on the facts, the applicable documents, and the goals of those involved.',
      'The firm provides direct attorney involvement and a practical, deliberate approach to real estate matters. A consultation is needed to review the circumstances and determine whether the firm can assist.',
    ],
    considerationsTitle: 'Discussing a real estate matter',
    considerations: [
      'The property and parties involved',
      'Relevant agreements, notices, and other documents',
      'The nature and current status of the issue',
      'Important dates and the desired objective',
    ],
  },
}

function Breadcrumbs({ current }: { current: string }) {
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <a href="/">Home</a>
      <span aria-hidden="true">/</span>
      <span aria-current="page">{current}</span>
    </nav>
  )
}

function PracticeAreaPage({ slug }: { slug: PracticeSlug }) {
  const page = practicePages[slug]
  const Icon = page.icon

  return (
    <>
      <a className="skip-link" href="#detail-content">Skip to main content</a>
      <SiteHeader />
      <main className="detail-main" id="detail-content">
        <section className="detail-hero">
          <div className="detail-hero-grid" aria-hidden="true" />
          <div className="container detail-hero-inner">
            <div>
              <Breadcrumbs current={page.eyebrow} />
              <p className="eyebrow">{page.eyebrow}</p>
              <h1>{page.title}</h1>
              <p className="detail-hero-lede">{page.intro}</p>
              <div className="detail-hero-actions">
                <a className="button button-gold" href="/#contact">
                  Discuss your matter <ArrowRight size={17} aria-hidden="true" />
                </a>
                <a className="detail-phone-link" href={phoneHref}>
                  <Phone size={17} aria-hidden="true" /> Call {phoneDisplay}
                </a>
              </div>
            </div>
            <div className="detail-hero-mark" aria-hidden="true">
              <Icon />
            </div>
          </div>
        </section>

        <section className="section detail-content-section">
          <div className="container detail-layout">
            <article className="detail-copy">
              <p className="eyebrow dark">How the firm can help</p>
              <h2>{page.overviewTitle}</h2>
              {page.overview.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}

              <div className="detail-considerations">
                <h2>{page.considerationsTitle}</h2>
                <p>
                  An initial conversation can help identify the information needed to
                  evaluate the inquiry. Depending on the matter, relevant topics may include:
                </p>
                <ul>
                  {page.considerations.map((item) => (
                    <li key={item}><CheckCircle2 aria-hidden="true" /><span>{item}</span></li>
                  ))}
                </ul>
              </div>

              <div className="detail-disclaimer" role="note">
                <Scale aria-hidden="true" />
                <p>
                  Every legal matter is different. This page provides general information,
                  not legal advice or a prediction of any result. A consultation is needed
                  to determine whether the firm can assist.
                </p>
              </div>
            </article>

            <aside className="detail-sidebar" aria-label="Attorney credential">
              <div className="detail-sidebar-card">
                <Award aria-hidden="true" />
                <p className="sidebar-label">Attorney credential</p>
                <h2>Board Certified in Civil Trial Law</h2>
                <p>Robert W. “Bob” Buchholz has been licensed in Texas since 1979.</p>
                <a href="/attorney">View attorney profile <ArrowRight size={15} aria-hidden="true" /></a>
              </div>
            </aside>
          </div>
        </section>

        <section className="detail-cta">
          <div className="container detail-cta-inner">
            <div>
              <p className="eyebrow">Contact the firm</p>
              <h2>Start a conversation about your legal matter.</h2>
            </div>
            <a className="button button-gold" href="/#contact">Contact the office</a>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  )
}

function AttorneyPage() {
  const admissions = [
    'U.S. Supreme Court',
    'Fifth Circuit Court of Appeals',
    'Tenth Circuit Court of Appeals',
    'U.S. District Courts in Texas',
    'U.S. District Courts in Oklahoma',
  ]

  return (
    <>
      <a className="skip-link" href="#detail-content">Skip to main content</a>
      <SiteHeader />
      <main className="detail-main" id="detail-content">
        <section className="detail-hero attorney-detail-hero">
          <div className="detail-hero-grid" aria-hidden="true" />
          <div className="container detail-hero-inner">
            <div>
              <Breadcrumbs current="Robert W. Buchholz" />
              <p className="eyebrow">Attorney profile</p>
              <h1>Robert W. “Bob” <span>Buchholz.</span></h1>
              <p className="detail-hero-lede">
                A Dallas attorney focused on commercial litigation, personal injury
                litigation, and real estate matters.
              </p>
              <div className="detail-hero-actions">
                <a className="button button-gold" href="/#contact">
                  Contact the office <ArrowRight size={17} aria-hidden="true" />
                </a>
                <a className="detail-phone-link" href={barProfile} target="_blank" rel="noreferrer">
                  Official State Bar profile <ExternalLink size={15} aria-hidden="true" />
                </a>
              </div>
            </div>
            <div className="attorney-detail-mark" aria-label="Robert W. Buchholz monogram">
              <span>RWB</span>
              <small>Attorney at Law</small>
            </div>
          </div>
        </section>

        <section className="section detail-content-section">
          <div className="container detail-layout attorney-detail-layout">
            <article className="detail-copy">
              <p className="eyebrow dark">Professional background</p>
              <h2>More than four decades in the legal profession.</h2>
              <p>
                Robert W. “Bob” Buchholz was licensed to practice law in Texas on
                November 12, 1979. He is Board Certified in Civil Trial Law and
                concentrates his practice in commercial litigation, personal injury
                litigation, and real estate.
              </p>
              <p>
                Bob earned his law degree from Southern Methodist University in May 1979.
                He is also licensed in Arkansas and Oklahoma. His solo practice provides
                clients with direct attorney involvement and a deliberate approach to
                each matter.
              </p>

              <div className="attorney-detail-facts">
                <div><Award aria-hidden="true" /><span><strong>Board Certification</strong>Civil Trial Law</span></div>
                <div><Landmark aria-hidden="true" /><span><strong>Law School</strong>Southern Methodist University, May 1979</span></div>
                <div><Scale aria-hidden="true" /><span><strong>Texas Bar Number</strong>03290600</span></div>
              </div>

              <section className="attorney-admissions" aria-labelledby="admissions-heading">
                <h2 id="admissions-heading">Licenses and court admissions</h2>
                <p>
                  Bob is licensed in Texas, Arkansas, and Oklahoma and reports admission
                  to the following federal courts on his State Bar of Texas profile:
                </p>
                <ul>
                  {admissions.map((admission) => (
                    <li key={admission}><CheckCircle2 aria-hidden="true" />{admission}</li>
                  ))}
                </ul>
              </section>

              <section className="attorney-personal" aria-labelledby="personal-heading">
                <h2 id="personal-heading">Beyond the office</h2>
                <p>
                  Outside his legal practice, Bob is a devoted husband and proud father
                  of three sons. His family is an important part of his life.
                </p>
              </section>

              <a className="button attorney-profile-button" href={barProfile} target="_blank" rel="noreferrer">
                Verify official profile <ExternalLink size={16} aria-hidden="true" />
              </a>
            </article>
          </div>
        </section>

        <section className="detail-cta">
          <div className="container detail-cta-inner">
            <div>
              <p className="eyebrow">Get in touch</p>
              <h2>Discuss whether the firm may be able to assist.</h2>
            </div>
            <a className="button button-gold" href="/#contact">Contact the office</a>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  )
}

export { AttorneyPage, PracticeAreaPage }
export type { PracticeSlug }
